import { ChevronDown } from 'lucide-react'
import { Fragment, useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardContent } from '@/components/ui/card.tsx'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select.tsx'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils.ts'

import { ShareDialog } from '@/components/ShareDialog.tsx'
import { UserAvatar } from '@/components/UserAvatar.tsx'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { useMutateProjectMembers } from '@/requests/mutations/useMutateProjectMembers.ts'
import { useQueryProjectMembers, useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'

export const MembersPage = () => {
  const { user } = useUserContext()
  const { members } = useQueryProjectMembers()
  const { projectMeta } = useQueryProjectMeta()

  if (!projectMeta) return

  const isCurrentUser = (memberId: string) => user?.id?.toString() === memberId

  const activeMembers = members.filter((m) => m.status === 'ACTIVE')
  const otherActiveMembers = activeMembers.filter((m) => m.id !== user?.id.toString())

  return (
    <div className="h-full">
      <main className="flex flex-col items-center p-6 max-w-[1078px] mx-auto w-full">
        <div className="flex justify-between items-center w-full mb-4">
          <h1 className="font-EN-KR-heading2-bold text-contents-primary">Members</h1>
          <ShareDialog link={projectMeta.invitationLink}>
            <Button variant="primary" className="font-EN-KR-body1-bold">
              Copy Project Link
            </Button>
          </ShareDialog>
        </div>

        <Card className="w-full p-4 bg-surface-backgroundElevated rounded-sm">
          <CardContent className="p-0 flex flex-col gap-2">
            {activeMembers
              .sort((a, b) => (a.name.toLowerCase() <= b.name.toLowerCase() ? -1 : 1))
              .map((m, index) => {
                return (
                  <Fragment key={m.id}>
                    <MemberCard projectId={projectMeta.id} member={m} isCurrentUser={isCurrentUser(m.id)} />
                    {index < activeMembers.length - 1 && <Separator />}
                  </Fragment>
                )
              })}
          </CardContent>

          {otherActiveMembers.length === 0 && (
            <>
              <Separator />
              <CardContent className="p-0">
                <div className="flex justify-center items-center py-12 px-4 text-center">
                  <p className="font-EN-KR-sub-heading-regular">
                    <span className="text-contents-secondary">It's lonely in here! </span>
                    <ShareDialog link={projectMeta.invitationLink}>
                      <span className="text-interactive-textLink font-EN-KR-sub-heading-bold cursor-pointer">
                        Copy project link to invite others.
                      </span>
                    </ShareDialog>
                  </p>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </main>
    </div>
  )
}

const MemberCard = ({
  projectId,
  member,
  isCurrentUser,
}: {
  projectId: string
  member: Member
  isCurrentUser: boolean
}) => {
  const possibleMemberRoles = {
    MEMBER: 'Member',
    ADMIN: 'Owner',
  } as const
  const { user } = useUserContext()
  const { members } = useQueryProjectMembers()
  const { updateRole, dropMember } = useMutateProjectMembers()
  const [open, setOpen] = useState(false)
  const [userToDrop, setUserToDrop] = useState<Member | null>(null)

  const hover = 'relative hover:after:bg-stateLayer-onLight-hovered hover:after:opacity-8 after:absolute after:inset-0'

  const getIcon = (option: MemberRole, role?: MemberRole) =>
    option === role ? <img src="/images/check-primary-icon.svg" alt="selected" /> : <div className="w-3.5 h-3.5" />

  const currentUser = members.find((m) => m.id === user?.id.toString())

  return (
    <div className="flex items-center justify-between px-2 py-1.5 ">
      <div className="flex items-center gap-3">
        <UserAvatar member={member} size="md" />

        <div className="flex flex-col">
          <div className="flex items-center gap-1">
            {isCurrentUser && (
              <Badge
                variant="secondary"
                className="font-EN-KR-caption-bold bg-surface-background text-contents-accent px-1 py-0 rounded-sm h-[14px] w-[26px] border-none"
              >
                You
              </Badge>
            )}
            <span className="font-EN-KR-body1-regular text-contents-primary text-xs leading-4">{member.name}</span>
          </div>
          <span className="font-EN-KR-body2-regular text-contents-secondary leading-[14px]">{member.email}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Select
          value={member.role}
          onValueChange={(newValue) => updateRole(projectId, member, newValue as MemberRole)}
          disabled={currentUser?.role !== 'ADMIN'}
        >
          <SelectTrigger
            aria-label={`role-${member.id}`}
            className={cn(
              'py-2 px-1.5 h-8 w-[82px] flex gap-1.5 items-center justify-center border-none',
              hover,
              'rounded-xs ring-rounded-2xs ring-offset-0 focus-visible:ring-offset-0 focus:ring-offset-0',
            )}
          >
            <span className="font-EN-KR-body2-regular text-contents-primary">
              {member.role && (member.role !== 'VIEWER' ? possibleMemberRoles[member.role] : 'Viewer')}
            </span>
            <ChevronDown className="h-3.5 w-3.5" />
          </SelectTrigger>
          <SelectContent className="min-w-[82px] w-[82px] py-1 rounded-xs">
            {Object.entries(possibleMemberRoles).map(([roleKey, label]) => (
              <SelectItem
                key={`${member.id}-${roleKey}`}
                value={roleKey}
                className={cn(
                  'w-[82px] py-2 px-1.5 flex items-center justify-start gap-1 cursor-pointer rounded-none',
                  hover,
                )}
              >
                <div className="flex gap-1 items-center">
                  {getIcon(roleKey as MemberRole, member.role)}
                  <span className="font-EN-KR-caption-regular text-contents-primary">{label}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {isCurrentUser || currentUser?.role === 'ADMIN' ? (
          <Button
            type="button"
            onClick={() => {
              setOpen(true)
              setUserToDrop(member)
            }}
            variant="ghost"
            size="icon"
            className="w-5 h-5"
            aria-label={`drop-${member.id}`}
          >
            <img src={isCurrentUser ? '/images/leave-project-icon.svg' : '/images/close-dialog-icon.svg'} alt="drop" />
          </Button>
        ) : (
          <div className="w-5 h-5" />
        )}
      </div>
      {userToDrop && (
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v)
            setUserToDrop(null)
          }}
        >
          <DialogContent className="p-4 gap-6 [&>button]:hidden">
            <DialogHeader>
              <DialogTitle className="font-EN-KR-sub-heading-bold text-contents-primary">
                {userToDrop?.id === user?.id.toString() ? 'Leaving Project?' : 'Remove this member?'}
              </DialogTitle>
              <DialogDescription className="font-EN-KR-body1-regular text-contents-primary">
                {userToDrop?.id === user?.id.toString()
                  ? 'You no longer will be able to access the project'
                  : 'This member will lose access to the project.'}
              </DialogDescription>
            </DialogHeader>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  if (userToDrop) {
                    dropMember(projectId, member).then(() => {
                      if (isCurrentUser) {
                        window.location.replace('/projects')
                      } else {
                        setOpen(false)
                        setUserToDrop(null)
                      }
                    })
                  }
                }}
              >
                {userToDrop?.id === user?.id.toString() ? 'I understand. Leave' : 'Remove'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
